/* eslint-disable react-hooks/exhaustive-deps */
import { loanRequestApi } from 'Api/loanRequest';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import dayjs from 'dayjs';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useContext, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import ConfirmModal from './ConfirmModal';
import NewLoanRequestModel from './NewLoanRequestModel';
import LoanRequestTableHeader from './Table/Header/index';
import LoanRequestTable from './Table/LoanRequestTable';
import styles from './index.module.scss';
import { PermissionContext } from 'components/Global/AuthLayout';
import Buttons from 'components/Global/Buttons';
import { SyncOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import UpdateLoanCreditModal from './UpdateLoanCreditModal';
import AddModal from './AddModal';

const LoanRequestComponent = () => {
	const [loanRequestData, setLoanRequestData] = useState<any[]>([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState('');
	const [date, setDate] = useState<any>(dayjs());
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deleteLoanId, setDeleteLoanId] = useState<string | null>(null);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [isNewLoanRequestModalOpen, setIsNewLoanRequestModalOpen] =
		useState(false);
	const [isConfirmLoanModal, setIsConfirmLoanModal] = useState(false);
	const [updateEffectiveDate, setUpdateEffectiveDate] = useState<any>(null);
	const [isUpdateLoanCreditModal, setIsUpdateLoanCreditModal] =
		useState(false);
	const [edit, setEdit] = useState<any>(null);
	const debouncedSearchValue = useDebounce(searchValue, 500);
	const [selectedPaymentMethod, setSelectedPaymentMethod] =
		useState<string>('');

	const [downloadLoading, setDownloadLoading] = useState(false);
	const permissionContext = useContext(PermissionContext);
	const isAdd =
		permissionContext.allowedPermissions.includes('Add_Loan_Request');

	// ===== NEW CHECKBOX STATES =====
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [selectedLoanKeys, setSelectedLoanKeys] = useState<string[]>([]);
	const [selectedCurrency, setSelectedCurrency] = useState<'ZMW' | 'USD'>(
		'ZMW'
	);
	const [isLoanLoading, setIsLoanLoading] = useState(false);
	const [loanDetailsData, setLoanDetailsData] = useState([]);
	const [isBankErrorModalVisible, setIsBankErrorModalVisible] =
		useState(false);

	const buttons = [
		{
			text: 'New Loan Request',
			isLoading: false,
			className: 'btn-blue',
			isSubmit: true,
			onclick: () => {
				setIsNewLoanRequestModalOpen(true);
			},
			disabled: isLoading,
		},
	];

	const handleSelectChange = (selectedRowKeys: string[]) => {
		setSelectedLoanKeys(selectedRowKeys);

		if (selectedRowKeys.length > 0) {
			const firstSelectedLoan = loanRequestData.find(
				(l) => l.id === selectedRowKeys[0]
			);
			if (firstSelectedLoan) {
				const newCurrency = firstSelectedLoan.isUsdEmployee
					? 'USD'
					: 'ZMW';
				setSelectedCurrency(newCurrency);
			}
		} else {
			setSelectedCurrency('ZMW'); // Reset to default
		}
	};

	// Fetch bank details based on currency (not employeeId)
	const fetchCompanyBankDetails = async (currency: 'ZMW' | 'USD') => {
		try {
			setIsLoanLoading(true);
			const bankDetails =
				await loanRequestApi.getBankDetailsForLoanDetails({
					currency: currency, // Pass currency instead of employeeId
				});
			if (bankDetails.data.data.length === 0) {
				setIsBankErrorModalVisible(true);
			} else {
				setIsModalVisible(true);
				setLoanDetailsData(bankDetails.data.data);
			}
		} catch (error: any) {
			let message = 'Something went wrong in fetching loan details';
			if (error.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			setIsLoanLoading(false);
		}
	};

	// Handle single payment (when user clicks $ icon)
	const handleModalOpen = (loanId: string) => {
		const loan = loanRequestData.find((l) => l.id === loanId);
		if (!loan) {
			toastText('Loan not found', 'error');
			return;
		}

		const currency = loan.isUsdEmployee ? 'USD' : 'ZMW';
		setSelectedLoanKeys([loanId]); // Single loan as array
		setSelectedCurrency(currency);
		fetchCompanyBankDetails(currency);
	};

	// Handle bulk payment (when user clicks Bulk Payment button)
	const handleBulkPayment = () => {
		if (selectedLoanKeys.length === 0) {
			toastText('Please select at least one loan', 'error');
			return;
		}

		// Verify all selected loans have the same currency
		const selectedLoans = loanRequestData.filter((loan) =>
			selectedLoanKeys.includes(loan.id)
		);

		const currencies = new Set(
			selectedLoans.map((loan) => (loan.isUsdEmployee ? 'USD' : 'ZMW'))
		);

		if (currencies.size > 1) {
			toastText(
				'Cannot process loans with different currencies together',
				'error'
			);
			return;
		}

		fetchCompanyBankDetails(selectedCurrency);
	};

	// ===== EXISTING HANDLERS =====

	const downloadLoanCsv = async () => {
		try {
			setDownloadLoading(true);
			const query = {
				month: dayjs(date).format('MM'),
				year: dayjs(date).format('YYYY'),
				paymentMethod: selectedPaymentMethod,
			};
			const loanRequest =
				await loanRequestApi.downloadEmployeeLoanExcelWithTemplate(query);
			const url = window.URL.createObjectURL(
				new Blob([loanRequest.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `Loan_employees_data.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			toastText(
				'Something went wrong in downloading csv template',
				'error'
			);
		} finally {
			setDownloadLoading(false);
		}
	};

	const cancelDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setDeleteLoanId(null);
	};

	const handleSearch = (value: string) => {
		setSearchValue(value);
		setCurrentPage(1);
	};

	const handleDatePicker = (value: any) => {
		setDate(value);
		setCurrentPage(1);
	};

	const handleSearchApi = async (value: string) => {
		//search api
	};

	const handleCancel = () => {
		setIsNewLoanRequestModalOpen(false);
	};

	const showDeleteModal = () => {
		setIsDeleteModalOpen(true);
	};

	const deleteHandler = async () => {
		if (deleteLoanId) {
			try {
				setIsDeleteLoading(true);
				await loanRequestApi.deleteLoanRequest(deleteLoanId);
				cancelDeleteModal();
				fetchLoanRequests();
				toastText('Loan deleted successfully', 'success');
			} catch (error: any) {
				let message =
					error?.response?.data?.message ||
					`Something went wrong in delete Loan Request`;
				toastText(message, 'error');
			} finally {
				setIsDeleteLoading(false);
			}
		}
	};

	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter
	) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		let sortOrderValue = '';
		if (sorter?.order === 'ascend') {
			sortOrderValue = 'asc';
		} else if (sorter?.order) {
			sortOrderValue = 'desc';
		}
		setSortOrder(sortOrderValue);
		setSortField(sorter.field);
	};

	const fetchLoanRequests = async () => {
		try {
			const query = {
				page: currentPage,
				searchText: debouncedSearchValue.trim(),
				sortBy: sortField,
				sortOrder: sortOrder,
				pageSize: pageSize,
				month: dayjs(date).format('MM'),
				year: dayjs(date).format('YYYY'),
				paymentMethod: selectedPaymentMethod,
			};
			setIsLoading(true);
			const loanRequest = await loanRequestApi.getLoanRequests(query);

			setLoanRequestData(loanRequest.data.data);
			setTotalRecords(loanRequest.data.total);

			// Reset selections when data changes
			setSelectedLoanKeys([]);
			setSelectedCurrency('ZMW');
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching loan request';
			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const updateLoanRequestStatus = async (id: string, newStatus: string) => {
		try {
			setIsLoading(true);
			const requestData = { id, status: newStatus };

			const response = await loanRequestApi.updateLoanRequestStatus(
				requestData.id,
				{ status: requestData.status }
			);

			if (response?.data) {
				const updatedData = loanRequestData.map((item) =>
					item.id === id ? { ...item, status: newStatus } : item
				);
				setLoanRequestData(updatedData);
				toastText('Status updated successfully', 'success');
			} else {
				toastText('Invalid response format', 'error');
			}
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				`An error occurred while updating status`;
			toastText(message, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const handleOk = () => {
		setIsDeleteModalOpen(false);
		setDeleteLoanId(null);
	};

	const confirmLoanModal = () => {
		setIsConfirmLoanModal(false);
		setIsUpdateLoanCreditModal(true);
	};

	const cancelLoanModal = () => {
		setIsConfirmLoanModal(false);
	};

	const handleEdit = (loanObj: any) => {
		setIsNewLoanRequestModalOpen(true);
		setEdit(loanObj);
	};

	const confirmLoanModalClick = () => {
		setIsConfirmLoanModal(true);
	};

	const cancelUpdateLoanCredit = () => {
		setIsUpdateLoanCreditModal(false);
		setUpdateEffectiveDate(null);
	};

	useEffect(() => {
		fetchLoanRequests();
	}, [
		currentPage,
		sortField,
		sortOrder,
		pageSize,
		debouncedSearchValue,
		date,
		selectedPaymentMethod,
	]);

	return (
		<div style={{ maxHeight: 'calc(100vh - 110px)' }}>
			<div className={styles['loan-header']}>
				<p> Loan Request</p>
				<div className={styles['loan-header__buttons']}>
					<Tooltip title="Refresh all the Leave Request">
						<SyncOutlined
							style={{
								fontSize: '20px',
								cursor: isLoading ? 'not-allowed' : 'pointer',
								color: '#584495',
								transition: 'transform 0.3s',
								marginRight: '10px',
							}}
							onClick={!isLoading ? fetchLoanRequests : undefined}
						/>
					</Tooltip>
					{isAdd && <Buttons buttons={buttons} />}
				</div>
			</div>
			<div className={styles['employee-container']}>
				<LoanRequestTableHeader
					searchText={searchValue}
					handleSearch={handleSearch}
					handleSearchApi={handleSearchApi}
					handleDatePicker={handleDatePicker}
					dateValue={date}
					totalRecords={totalRecords}
					downloadLoading={downloadLoading}
					downloadLoanCsv={downloadLoanCsv}
					// NEW PROPS for checkbox functionality
					selectedCount={selectedLoanKeys.length}
					selectedCurrency={selectedCurrency}
					onBulkPayment={handleBulkPayment}
					selectedPaymentMethod={selectedPaymentMethod}
					setSelectedPaymentMethod={setSelectedPaymentMethod}
				/>
				<LoanRequestTable
					tableData={loanRequestData}
					totalRecords={totalRecords}
					currentPage={currentPage}
					pageSize={pageSize}
					showDeleteModal={showDeleteModal}
					setDeleteLoan={setDeleteLoanId}
					handleEdit={handleEdit}
					tableChangeHandler={tableChangeHandler}
					isLoading={isLoading}
					updateLoanRequestStatus={updateLoanRequestStatus}
					confirmLoanModal={confirmLoanModalClick}
					setUpdateEffectiveDate={setUpdateEffectiveDate}
					handleModalOpen={handleModalOpen}
					// NEW PROPS for checkbox functionality
					selectedLoanKeys={selectedLoanKeys}
					onSelectChange={handleSelectChange}
					selectedCurrency={selectedCurrency}
					isBankErrorModalVisible={isBankErrorModalVisible}
					setIsBankErrorModalVisible={setIsBankErrorModalVisible}
				/>
			</div>
			{isNewLoanRequestModalOpen && (
				<NewLoanRequestModel
					isNewLoanRequestModalOpen={isNewLoanRequestModalOpen}
					handleCancel={handleCancel}
					fetchLoanRequest={fetchLoanRequests}
					edit={edit}
					setEdit={setEdit}
				/>
			)}

			{isUpdateLoanCreditModal && (
				<UpdateLoanCreditModal
					visible={isUpdateLoanCreditModal}
					onCancel={cancelUpdateLoanCredit}
					updateEffectiveDate={updateEffectiveDate}
					fetchLoanRequests={fetchLoanRequests}
				/>
			)}
			{isDeleteModalOpen && (
				<ConfirmDelete
					isModalOpen={isDeleteModalOpen}
					handleCancel={cancelDeleteModal}
					deleteHandler={deleteHandler}
					isLoading={isDeleteLoading}
					handleOk={handleOk}
				/>
			)}
			{isConfirmLoanModal && (
				<ConfirmModal
					visible={isConfirmLoanModal}
					onOk={confirmLoanModal}
					onCancel={cancelLoanModal}
					content={`Are you sure Loan has been Credited to [${updateEffectiveDate?.employeeName} | ${updateEffectiveDate?.employeeCode}] ?`}
					okText="Yes"
					cancelText="No"
				/>
			)}

			{isModalVisible && (
				<AddModal
					isOpen={isModalVisible}
					handleCancel={() => {
						setIsModalVisible(false);
						setSelectedLoanKeys([]);
					}}
					isBankLoading={isLoanLoading}
					bankDetailsData={loanDetailsData}
					// CHANGED: Pass array of loan IDs instead of single payRollId
					selectedLoanIds={selectedLoanKeys}
					handleBankDetails={setLoanDetailsData}
					fetchLoanRequests={fetchLoanRequests}
				/>
			)}
		</div>
	);
};

export default LoanRequestComponent;
